import React from 'react';
import { Eye, User, Calendar, AlertCircle } from 'lucide-react';
import DisputeStatusBadge from './DisputeStatusBadge';

const PRIORITY_CONFIG = {
  low: { label: 'Nizak', className: 'text-gray-500' },
  medium: { label: 'Srednji', className: 'text-amber-600' },
  high: { label: 'Visok', className: 'text-orange-600' },
  urgent: { label: 'Hitan', className: 'text-red-600 font-bold' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const DisputeListTable = ({ disputes, selectedId, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500">Učitavanje sporova...</span>
      </div>
    );
  }

  if (!disputes?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <AlertCircle className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-lg font-medium">Nema sporova</p>
        <p className="text-sm">Nema prijavljenih sporova za izabrani filter</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">ID</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Predmet spora</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Podnosilac</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Tuženi</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Datum prijave</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Prioritet</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {disputes?.map((dispute) => {
            const isSelected = selectedId === dispute?.id;
            const priority = PRIORITY_CONFIG?.[dispute?.priority] || PRIORITY_CONFIG?.medium;
            return (
              <tr
                key={dispute?.id}
                onClick={() => onSelect(dispute)}
                className={`cursor-pointer transition-colors duration-150 ${
                  isSelected
                    ? 'bg-blue-50 border-l-4 border-l-blue-500' :'hover:bg-gray-50 border-l-4 border-l-transparent'
                }`}
              >
                <td className="px-4 py-3 font-mono text-xs text-gray-500">
                  #{dispute?.id?.slice(0, 8)}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900 truncate max-w-[200px]">{dispute?.title || 'Bez naslova'}</p>
                  <p className="text-xs text-gray-500 capitalize">{dispute?.disputeCategory || '—'}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 truncate max-w-[120px]">
                      {dispute?.filedByProfile?.full_name || dispute?.filedByProfile?.email || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 truncate max-w-[120px]">
                      {dispute?.respondentProfile?.full_name || dispute?.respondentProfile?.email || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    {formatDate(dispute?.filedAt)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <DisputeStatusBadge status={dispute?.disputeStatus} />
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium ${priority?.className}`}>{priority?.label}</span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => { e?.stopPropagation(); onSelect(dispute); }}
                    className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                    title="Pregledaj spor"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DisputeListTable;
